import { useState, useEffect } from "react";
import Button from "../../../components/Button";
import { TaxPolicyPayload, TAX_POLICY_TYPE_OPTIONS } from "../types";
import { withIds } from "../utils";

type TaxPolicyWithId = TaxPolicyPayload & { _id: string };

const EMPTY_POLICY: TaxPolicyPayload = {
  name: "",
  type: "PERCENTAGE",
  amount: 0,
  isTax: true,
  appliesToBase: true,
  isRefundable: false,
};

export function TaxPolicyEditor({
  initialPolicies,
  onSave,
  isSaving,
}: {
  initialPolicies: TaxPolicyPayload[];
  onSave: (policies: TaxPolicyPayload[]) => void;
  isSaving: boolean;
}) {
  const [policies, setPolicies] = useState<TaxPolicyWithId[]>(() => withIds(initialPolicies));

  useEffect(() => {
    setPolicies(withIds(initialPolicies));
  }, [initialPolicies]);

  const updatePolicy = (index: number, field: keyof TaxPolicyPayload, value: string | number | boolean) => {
    setPolicies((prev) =>
      prev.map((p, i) => (i === index ? { ...p, [field]: value } : p))
    );
  };

  const removePolicy = (index: number) => {
    setPolicies((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-900">Tax Policies</h3>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPolicies((prev) => [...prev, { ...EMPTY_POLICY, _id: crypto.randomUUID() } as TaxPolicyWithId])}
          >
            Add Policy
          </Button>
          <Button
            size="sm"
            onClick={() => onSave(policies.map(({ _id, ...rest }) => rest))}
            loading={isSaving}
            disabled={isSaving}
          >
            Save All
          </Button>
        </div>
      </div>

      {policies.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-4">
          No tax policies configured.
        </p>
      ) : (
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F4F4F6]">
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase rounded-tl-lg rounded-bl-lg">
                  Name
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Type
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Amount
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                  Is Tax
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                  Applies to Base
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                  Refundable
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase rounded-tr-lg rounded-br-lg">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {policies.map((policy, idx) => (
                <tr key={policy._id} className="border-b border-b-[#E9E9EB]">
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={policy.name}
                      onChange={(e) => updatePolicy(idx, "name", e.target.value)}
                      className="w-full border border-neutral-400 rounded px-2 py-1 text-sm"
                      placeholder="Tax name"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={policy.type}
                      onChange={(e) => updatePolicy(idx, "type", e.target.value)}
                      className="w-full border border-neutral-400 rounded px-2 py-1 text-sm"
                    >
                      {TAX_POLICY_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      value={policy.amount}
                      onChange={(e) =>
                        updatePolicy(idx, "amount", parseFloat(e.target.value) || 0)
                      }
                      className="w-20 border border-neutral-400 rounded px-2 py-1 text-sm"
                    />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={policy.isTax}
                      onChange={(e) => updatePolicy(idx, "isTax", e.target.checked)}
                      className="w-4 h-4 cursor-pointer"
                    />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={policy.appliesToBase}
                      onChange={(e) =>
                        updatePolicy(idx, "appliesToBase", e.target.checked)
                      }
                      className="w-4 h-4 cursor-pointer"
                    />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={policy.isRefundable}
                      onChange={(e) =>
                        updatePolicy(idx, "isRefundable", e.target.checked)
                      }
                      className="w-4 h-4 cursor-pointer"
                    />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button
                      onClick={() => removePolicy(idx)}
                      className="text-red-600 hover:text-red-800 text-xs font-medium"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
